
import { ChevronLeft, ChevronRight, Target } from 'lucide-react';
import { useState, useEffect } from 'react';
import health from '../../Assets/Images/rewamp/health goal.svg';

const goalOptions = [
  'Weight loss',
  'Weight Gain',
  'Maintain Weight',
  'Muscle Gain',
  'Fat Loss',
  'Body Toning',
  'Improve Stamina',
];

const mealPlanOptions = [
  'Value Friendly Choices',
  'Quick & Easy',
  'Comfort Choices',
  'Seasonal Food',
];

export default function HealthGoals({ onNext, onPrevious, patientData = {} }) {
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [otherGoal, setOtherGoal] = useState('');
  const [goalError, setGoalError] = useState('');
  const [selectedMealPlans, setSelectedMealPlans] = useState(['Value Friendly Choices']);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (patientData?.goal) {
      const goalsArray = patientData.goal.split(', ').filter(g => g.trim());
      setSelectedGoals(goalsArray);
    }
    if (patientData?.meal_plan) {
      const mealPlansArray = patientData.meal_plan.split(', ').filter(mp => mp.trim());
      setSelectedMealPlans(mealPlansArray.length > 0 ? mealPlansArray : ['Value Friendly Choices']);
    }
  }, [patientData]);

  const predefinedGoalsCount = selectedGoals.filter(g => goalOptions.includes(g)).length;
  const customGoalsCount = selectedGoals.filter(g => !goalOptions.includes(g)).length;
  const canAddMore = selectedGoals.length < 5;

  const toggleGoal = (goal) => {
    setSelectedGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
    );
    if (errors.goals) {
      setErrors({ ...errors, goals: '' });
    }
  };

  const toggleMealPlan = (mealPlan) => {
    setSelectedMealPlans((prev) =>
      prev.includes(mealPlan) ? prev.filter((mp) => mp !== mealPlan) : [...prev, mealPlan]
    );
    if (errors.mealPlan) {
      setErrors({ ...errors, mealPlan: '' });
    }
  };

  const removeGoal = (goal) => {
    setSelectedGoals((prev) => prev.filter((g) => g !== goal));
  };

  const addOtherGoal = () => {
    if (otherGoal.trim() && selectedGoals.length < 5) {
      setSelectedGoals([...selectedGoals, otherGoal]);
      setOtherGoal('');
    }
  };

  const validate = () => {
    const newErrors = {};
    if (selectedGoals.length === 0) {
      newErrors.goals = 'Please select at least one health goal';
    }
    if (selectedMealPlans.length === 0) {
      newErrors.mealPlan = 'Please select at least one meal plan type';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      const goalString = selectedGoals.join(', ');
      const mealPlanString = selectedMealPlans.join(', ');
      const dataToSend = {
        goal: goalString,
        meal_plan: mealPlanString,
      };
      console.log("HealthGoals sending:", dataToSend);
      onNext(dataToSend);
    }
  };

  const handlePrevious = () => {
    // Save current selections before going back
    const goalString = selectedGoals.join(', ');
    const mealPlanString = selectedMealPlans.join(', ');
    const dataToSend = {
      goal: goalString,
      meal_plan: mealPlanString,
    };
    console.log("HealthGoals saving before previous:", dataToSend);
    onPrevious(dataToSend);
  };

  const buttonClass =
    'rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors cursor-pointer';
  const unselectedButtonClass =
    'border-[#004276] bg-White text-[#004276] hover:border-[#003360] hover:bg-[#F1F8FD]';
  const disabledButtonClass =
    'cursor-not-allowed opacity-50';

  return (
    <div 
      className="py-2 sm:py-3 px-2 sm:px-4"
      style={{
        backgroundColor: '#fff',
        backgroundImage: 'var(--section-bg-gradient)'
      }}
    >
      {/* Main Container - Full Width */}
      <div className="w-full max-w-full mx-auto">
        
        {/* Section Header */}
        

        <div className="mb-5 sm:mb-6 flex items-center gap-3 sm:gap-4 px-2 sm:px-4">
  
  {/* ICON (replaced) */}
  <div className="rounded-xl bg-[#EAF3FA] p-2.5 flex items-center justify-center shrink-0">
    <img 
      src={health} 
      alt="Basic Info" 
       className="h-12 w-12 sm:h-14 sm:w-14 object-contain"

    />
  </div>

  {/* TEXT */}
  <div className="flex-1 min-w-0">
    <h2 
      className="text-lg sm:text-2xl font-bold break-words"
      style={{ color: "var(--color-brand-primary)" }}
    >
      Health Goals
    </h2>

    <p className="text-sm sm:text-base text-textGray mt-1 break-words">
      Help us understand your health conditions and requirements
    </p>
  </div>

</div>

        {/* Form Container */}
        <div className="px-0 sm:px-4 lg:px-8">
          
          {/* Goals Selection */}
          <div className="mb-6 sm:mb-8">
            <div className="mb-4 sm:mb-6">
              <label className="block text-sm sm:text-base font-bold text-quantumPrimary mb-3 sm:mb-4">
                What are your Health and Fitness Goals? (Select Up to 4)
                <span className="text-red-500 ml-1">*</span>
              </label>
              {errors.goals && (
                <p className="text-red-500 text-sm mb-4">{errors.goals}</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3 lg:gap-4">
              {goalOptions.map((goal) => (
                <button
                  key={goal}
                  type="button"
                  onClick={() => toggleGoal(goal)}
                  disabled={
                    predefinedGoalsCount >= 4 && !selectedGoals.includes(goal)
                  }
                  className={`${buttonClass} ${
                    selectedGoals.includes(goal)
                      ? ''
                      : unselectedButtonClass
                  } ${
                    predefinedGoalsCount >= 4 && !selectedGoals.includes(goal)
                      ? disabledButtonClass
                      : ''
                  } px-4 sm:px-5 lg:px-6 py-2 sm:py-2.5 lg:py-3 text-sm sm:text-base font-medium transition-all`}
                  style={
                    selectedGoals.includes(goal)
                      ? {
                          background: 'var(--color-brand-primary)',
                          color: '#fff',
                        }
                      : {}
                  }
                >
                  {goal}
                </button>
              ))}
              
              {/* Custom added goals */}
              {selectedGoals.map((goal) => (
                !goalOptions.includes(goal) && (
                  <div
                    key={goal}
                    className="text-White px-4 sm:px-5 lg:px-6 py-2 sm:py-2.5 lg:py-3 rounded-full text-sm sm:text-base flex items-center gap-2 sm:gap-3 font-medium"
                    style={{
                      background: 'var(--color-brand-primary)'
                    }}
                  >
                    {goal}
                    <button
                      type="button"
                      onClick={() => removeGoal(goal)}
                      className="hover:opacity-70 font-bold text-lg sm:text-xl leading-none"
                    >
                      ×
                    </button>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Other Goal Input */}
          <div className="mb-6 sm:mb-8">
            <label className="block text-sm sm:text-base font-bold text-quantumPrimary mb-3 sm:mb-4">
              Other (Specify) {customGoalsCount > 0 && <span className="text-textGray font-normal">({customGoalsCount}/1)</span>}
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                placeholder="Enter here"
                value={otherGoal}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only alphanumeric and spaces
                  if (/^[a-zA-Z\s]*$/.test(value)) {
                    setOtherGoal(value);
                    setGoalError('');
                  } else {
                    setGoalError('Special characters are not allowed');
                  }
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addOtherGoal();
                  }
                }}
                disabled={customGoalsCount >= 1 || !canAddMore}
                className="flex-1 px-4 sm:px-5 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm sm:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                type="button"
                onClick={addOtherGoal}
                disabled={!otherGoal.trim() || customGoalsCount >= 1 || !canAddMore}
                className="px-6 sm:px-8 py-2.5 sm:py-3 text-[#004276] rounded-lg font-semibold text-sm sm:text-base transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                style={{
                  backgroundColor: '#ffff'
                }}
                onMouseEnter={(e) => !e.currentTarget.disabled && (e.currentTarget.style.opacity = '0.8')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                Add
              </button>
            </div>
            {goalError && <p className="text-red-500 text-sm mt-1">{goalError}</p>}
          </div>

          {/* Meal Plan Selection */}
          <div className="mb-6 sm:mb-8 p-6 bg-white rounded-xl ">
            <div className="mb-4 sm:mb-6 flex items-center gap-3">
              <div className="rounded-lg bg-lightGreen p-2">
                <svg className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: '#42BA85' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <label className="block text-sm sm:text-base font-bold text-quantumPrimary">
                Choose the type of Meal Plan you prefer
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
            
            {errors.mealPlan && (
              <p className="text-red-500 text-sm mb-4">{errors.mealPlan}</p>
            )}

           <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
  {mealPlanOptions.map((plan) => (
    <label
      key={plan}
      className="flex items-center gap-2 sm:gap-3 cursor-pointer group"
    >
      <div className="relative">
        <input
          type="radio"
          name="mealPlan"
          checked={selectedMealPlans[0] === plan}
          onChange={() => setSelectedMealPlans([plan])}
          className="peer sr-only"
        />
        <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-gray-300 rounded-full peer-checked:border-[#42BA85] peer-checked:bg-[#42BA85] transition-all flex items-center justify-center">
          {selectedMealPlans[0] === plan && (
            <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-white rounded-full" />
          )}
        </div>
      </div>
      <span className="text-sm sm:text-base text-textGray group-hover:text-quantumPrimary transition-colors">
        {plan}
      </span>
    </label>
  ))}
</div>

          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0 mt-8 sm:mt-12 lg:mt-16 pt-6 sm:pt-8 border-t border-gray-200">
            <button
              type="button"
              onClick={handlePrevious}
              className="flex items-center justify-center gap-2 rounded-full border-2 border-[#99B3C8] bg-White px-6 sm:px-8 py-2.5 sm:py-3 font-semibold text-[#004276] transition hover:bg-[#F1F8FD] hover:border-[#004276] text-sm sm:text-base"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              Previous
            </button>
            <button
  type="button"
  onClick={handleSubmit}
  className="flex items-center justify-center gap-2 rounded-full px-8 sm:px-10 py-2.5 sm:py-3 font-semibold text-white transition shadow-lg text-sm sm:text-base"
  style={{ background: "var(--color-brand-primary)" }}
  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
  onMouseLeave={(e) => e.target.style.opacity = '1'}
>
  Next <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
</button>
          </div>
        </div>
      </div>
    </div>
  );
}




